import {
  getInvoiceWithouthRelation,
  rejectProcessFile,
  postUpdateUbl,
  postReprocessInvoiceWithouthRelations,
  updateAndAttachUbl,
} from '@/lib/adapters/invoice-adapter';
import { getRelations } from '@/lib/adapters/users-adapter';
import {
  Accent,
  CustomButton,
  CustomColumnType,
  CustomTable,
  DocumentViewModal,
  InvoiceRejectModal,
} from '@/lib/components';
import { useGetIdentifierTypes } from '@/lib/queries';
import {
  InvoiceWithouthRelation,
  RejectFile,
  RejectModalState,
} from '@/lib/types';
import { IdentifierCategory } from '@/lib/types/enums';
import { Relation, RelationIdentifier } from '@/lib/types/user';
import { showNotification } from '@/lib/utils/showNotification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const DEFAULT_LAYOUT = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const DETAILS_COLUMN_WIDTH = '360px';
const RELATIONS_COLUMN_WIDTH = '520px';
const FORM_COLUMN_WIDTH = '380px';

const descriptionsStyles = {
  label: { fontWeight: 'bold', whiteSpace: 'nowrap' },
  content: { wordBreak: 'break-word' },
} as const;

export const AttachUnknownRelation = () => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [modalState, setModalState] = useState<RejectModalState>('hidden');

  const [invoice, setInvoice] = useState<InvoiceWithouthRelation>();
  const [possibleRelations, setPossibleRelations] = useState<Relation[]>();

  const [form] = Form.useForm();
  const currentIdentifiers = Form.useWatch(['identifiers'], form);

  const { id: invoiceId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: identifierTypes } = useGetIdentifierTypes();

  const onFormFinish = (values) => {
    const mappedRelationIdentifiers = {
      ...values,
      type: 'external',
    };
    setLoading(true);
    if (!invoice) return;
    postUpdateUbl(invoice.id, mappedRelationIdentifiers)
      .then(() => handleSuccess())
      .catch((e) => {
        showNotification(
          'error',
          'Fout bij opslaan, probeer opnieuw te verwerken.',
        );
      })
      .finally(() => setLoading(false));
  };

  const handleAddIdentifier = (relationId: string) => {
    if (!invoice) return;
    setLoading(true);
    updateAndAttachUbl(invoice.id, relationId, currentIdentifiers)
      .then(() => handleSuccess())
      .finally(() => setLoading(false));
  };

  const handleSuccess = () => {
    setLoading(true);
    if (invoice)
      postReprocessInvoiceWithouthRelations(currentIdentifiers).then(() => {
        navigate('/settings/invoiceswithouthrelation');
      });
  };

  const rejectFile = async (
    file: RejectFile,
    reason: string,
    description?: string,
  ) => {
    try {
      await rejectProcessFile(file.id as string, reason, description);
      showNotification('success', 'Afgewezen.');
      closeRejectModal();
    } catch {
      showNotification('error', 'Fout tijdens afwijzen.');
    }
  };

  const possibleTableColumns: CustomColumnType<Relation>[] = [
    {
      title: t('relation.name'),
      dataIndex: 'name',
    },
    {
      title: t('relation.address'),
      render: (text, relation) =>
        `${relation.street} ${relation.houseNumber}, ${relation.zipcode}`,
    },
    {
      title: t('relation.city'),
      dataIndex: 'city',
      width: 200,
    },
    {
      title: t('relation.coc'),
      key: 'coc',
      dataIndex: 'identifiers',
      sorter: false,
      render: (identifiers: RelationIdentifier[]) =>
        identifiers?.find(
          (identifier) => identifier.category === IdentifierCategory.COC,
        )?.identifier,
    },
    {
      title: t('relation.vat'),
      key: 'vat',
      dataIndex: 'identifiers',
      sorter: false,
      render: (identifiers: RelationIdentifier[]) =>
        identifiers?.find(
          (identifier) => identifier.category === IdentifierCategory.VAT,
        )?.identifier,
    },
    {
      render: (text, relation) => (
        <CustomButton
          size="small"
          color="secondary"
          toolTipKey="linkrelations.action.linkSelected"
          onClick={() => handleAddIdentifier(relation.id)}
        >
          Koppelen
        </CustomButton>
      ),
    },
  ];

  useEffect(() => {
    const fetchInvoiceData = async (id: string) => {
      setLoading(true);
      try {
        const invoiceRelationData = await getInvoiceWithouthRelation(id);
        setInvoice(invoiceRelationData);
        form.setFieldsValue(invoiceRelationData.relation);

        const possibleRelationsData = await getRelations({
          zipcode: invoiceRelationData.relation.zipcode,
        });

        setPossibleRelations(possibleRelationsData?.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceData(invoiceId!);
  }, [form, invoiceId]);

  const openRejectModal = () => {
    setModalState('single');
  };

  const closeRejectModal = () => {
    setModalState('hidden');
  };

  return (
    <>
      <InvoiceRejectModal
        modalState={modalState}
        hideModal={closeRejectModal}
        singleFile={
          invoice
            ? {
                id: invoice.id,
                name: invoice?.documentName,
              }
            : undefined
        }
        rejectSingle={rejectFile}
        multiFiles={[]}
      />
      <Spin spinning={isLoading}>
        <Row gutter={[24, 24]} wrap align="top">
          <Col span={24} style={{ marginBottom: '1rem' }}>
            <CustomButton
              icon={<FontAwesomeIcon icon="long-arrow-alt-left" />}
              color="secondary"
              ghost
              onClick={() => navigate('/settings/invoiceswithouthrelation')}
            >
              Ga terug
            </CustomButton>
            {invoice && (
              <CustomButton
                ghost
                shape="circle"
                toolTipKey="linkrelations.action.reject"
                danger
                onClick={() => openRejectModal()}
                size="small"
                icon={<FontAwesomeIcon icon="ban" />}
              />
            )}
          </Col>
          <Col flex={`1 1 ${DETAILS_COLUMN_WIDTH}`} style={{ minWidth: 0 }}>
            <Accent type="h3" color="primary">
              Factuurgegevens
            </Accent>

            <Descriptions styles={descriptionsStyles} colon={false} column={1}>
              <Descriptions.Item span={3} label={t('relation.name')}>
                {invoice?.relation.name}
              </Descriptions.Item>
              <Descriptions.Item span={3} label={t('relation.address')}>
                {invoice?.relation.street}, {invoice?.relation.city}{' '}
                {invoice?.relation.country}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={t('invoiceWithouthRelation.documentName')}
              >
                {invoice?.documentName}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={t('invoiceWithouthRelation.supplierIdentifier')}
              >
                {invoice?.supplierIdentifier} -{' '}
                {invoice?.supplierIdentifierType}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={t('invoiceWithouthRelation.customerIdentifier')}
              >
                {invoice?.customerIdentifier} -{' '}
                {invoice?.customerIdentifierType}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={t('invoiceWithouthRelation.uploadType')}
              >
                {invoice?.uploadType}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={t('invoiceWithouthRelation.showPdf')}
              >
                {invoice && (
                  <DocumentViewModal id={invoice.id} idType="processfile" />
                )}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col flex={`2 1 ${RELATIONS_COLUMN_WIDTH}`} style={{ minWidth: 0 }}>
            <Accent type="h3" color="primary">
              Relaties met overeenkomstige postcode
            </Accent>
            <div style={{ overflowX: 'auto' }}>
              <CustomTable
                locale={{ emptyText: 'Er zijn geen relaties gevonden' }}
                dataSource={possibleRelations}
                columns={possibleTableColumns}
              />
            </div>
          </Col>
          <Col flex={`1 1 ${FORM_COLUMN_WIDTH}`} style={{ minWidth: 0 }}>
            <Accent type="h3" color="primary">
              Nieuwe relatie maken
            </Accent>

            <Accent type="small" style={{ marginBottom: '1rem' }}>
              De nieuwe relatie wordt gemaakt op basis van gevonden gegevens uit
              het bestand. Je kunt hieronder de gegevens aanpassen of aanvullen.
            </Accent>

            <Form form={form} {...DEFAULT_LAYOUT} onFinish={onFormFinish}>
              <Form.Item name="name" label={t('relation.name')}>
                <Input />
              </Form.Item>
              <Form.Item name="zipcode" label={t('relation.zipcode')}>
                <Input />
              </Form.Item>
              <Form.Item name="street" label={t('relation.street')}>
                <Input />
              </Form.Item>
              <Form.Item name="housenumber" label={t('relation.housenumber')}>
                <Input />
              </Form.Item>
              <Form.Item name="city" label={t('relation.city')}>
                <Input />
              </Form.Item>
              <Form.Item name="country" label={t('relation.country')}>
                <Input />
              </Form.Item>

              <Divider plain>Identifiers</Divider>

              <Form.List name="identifiers">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space.Compact
                        block
                        key={key}
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'identifier']}
                          label={t('relation.identifier.identifier')}
                          style={{ width: '60%' }}
                        >
                          <Input />
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'identifierType']}
                          style={{ width: '30%', paddingLeft: '20px' }}
                        >
                          <Select
                            showSearch
                            options={(identifierTypes || [])
                              .filter(
                                (type) =>
                                  type.category !== IdentifierCategory.CUSTOMER,
                              ) // Filter out CUSTOMER category
                              .map((type) => ({
                                label: type.type,
                                value: type.type,
                              }))}
                          />
                        </Form.Item>
                        <CustomButton
                          type="link"
                          danger
                          shape="circle"
                          size="small"
                          onClick={() => remove(name)}
                          icon={<FontAwesomeIcon icon="trash-alt" />}
                        />
                      </Space.Compact>
                    ))}

                    <Form.Item
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <CustomButton
                        color="secondary"
                        ghost
                        onClick={() => add()}
                        icon
                      >
                        Identifier toevoegen
                      </CustomButton>
                    </Form.Item>
                  </>
                )}
              </Form.List>

              <Form.Item label=" " colon={false}>
                <Row justify="space-between">
                  <Col></Col>
                  <CustomButton
                    htmlType="submit"
                    type="primary"
                    color="secondary"
                    style={{ marginTop: '1rem' }}
                    toolTipKey="linkrelations.action.createNew"
                  >
                    {t('general.actions.save')}
                  </CustomButton>
                </Row>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Spin>
    </>
  );
};
